/**
 * Navigation cible v0.5.0 — lot U6b, VAGUE 1 (ARCHITECTURE-INFORMATION-v4 § 4.3, § 4.9, § 4.11 ;
 * planches `Nav`, `BarreLaterale`, `Coquille`).
 *
 * QUATRE NIVEAUX, chacun avec sa propre barre latérale : Plateforme (super-administrateur seul),
 * Organisation, Programme, Projet. Le niveau se lit dans la route (`/plateforme/*`,
 * `/programme/*`, écrans d'organisation, écrans du projet) ; une route transverse (Accueil,
 * Boîte d'attention, Notifications…) garde le niveau où l'on était (`useNiveau`).
 *
 * - Projet : Vue projet, les 5 GROUPES (Concevoir ①②③④, Planifier ⑤, Construire ⑥, Vérifier ⑦,
 *   Livrer et exploiter ⑧⑨), leurs étapes en sous-titres non cliquables, puis Réglages.
 * - Organisation : ≤ 13 entrées de premier niveau (Accueil compris), dont le groupe
 *   « Référentiel et technologies » (ARB-r2-21, AC-N1).
 * - Les entrées 0.5.x restent VISIBLES avec leur marque « 0.5.x — conçu, livré après la v0.5.0 »
 *   et ouvrent un écran qui le dit ; jamais un retrait silencieux (PLAN-U6B § 6).
 * - Les entrées bêta dont l'écran n'existe pas encore (`etat: 'a-venir'`) ouvrent le même écran
 *   d'attente, qui nomme la vague qui le livre et l'endroit où la fonction vit aujourd'hui.
 *
 * Une seule source, consommée par la barre latérale, la palette ⌘K, le fil d'Ariane, le titre
 * d'onglet, le routeur (écrans d'attente, redirections) et les tests. La visibilité suit le RÔLE
 * D'ACCÈS (`minRole`/`ownerOnly`/`superAdmin`) ; le PROFIL ne change que l'accueil.
 *
 * Les `navKey` sont un contrat serveur (`NotificationVisibility.java`, badges de notifications
 * par destination) : ils suivent l'ÉCRAN, jamais son rangement ni son libellé.
 */
import {
  LayoutDashboard, Activity, History, Lightbulb, Upload, MessagesSquare,
  FileText, Workflow, GitCompareArrows, Columns3, ClipboardCheck, Rocket,
  Network, Package, Star, Settings, Gauge, Bell, Waypoints, BookText, Layers, Compass,
  SlidersHorizontal, Server, FlaskConical, List, CalendarRange, IterationCcw, Share2, ListChecks,
  TimerReset, Bug, Landmark, Scale, Inbox, Bot, Tag, AppWindow, Palette, Home, Boxes, Users,
  ShieldCheck, FileCheck, HeartPulse, Building2, Globe2, OctagonX, Cpu, Archive, KeyRound,
  Radar, GitBranch, Eye, Blocks, PackageCheck, BadgeCheck, Receipt, Hammer, ListTree, FolderKanban, MailOpen, ListTodo, Siren,
  CalendarClock, FileBarChart,
  type LucideIcon,
} from 'lucide-react'
import type { NavigationMode } from '@/lib/navigationMode'

/** Rôles de l'espace, du moins au plus capable. */
export type Role = 'viewer' | 'contributor' | 'owner'

/** Les quatre niveaux de la navigation cible (§ 4.3). */
export type Niveau = 'plateforme' | 'organisation' | 'programme' | 'projet'

/** `a-venir` : bêta, écran pas encore construit (une vague ultérieure du lot U6b le livre).
 *  `0.5.x` : conçu, livré après la v0.5.0 — affiché avec sa marque, jamais masqué. */
export type EtatEntree = 'a-venir' | '0.5.x'

export interface NavItem {
  label: string
  /** Chemin de l'écran ; peut porter une requête (`/projet?vue=tableau`). */
  path: string
  /** Identifiant stable du contrat de notifications (ne jamais dériver depuis path). */
  navKey: string
  icon: LucideIcon
  /** Phrase courte affichée dans la palette de commandes (et en infobulle). */
  description: string
  /** Route exacte (pour l'accueil sur `/`). */
  end?: boolean
  /** Visible du propriétaire de l'espace et de l'administrateur global seulement. */
  ownerOnly?: boolean
  /** Visible de l'administrateur de la plateforme seulement (rôle `admin`). */
  superAdmin?: boolean
  /** Cadenas « réservé à l'administration » (planche `Nav`) ; implique `ownerOnly`. */
  reserveAdministration?: boolean
  /**
   * Lot USR (ARB-SEC-USR-01/05) : visible de l'administrateur d'ORGANISATION (`org_admin`), de
   * l'auditeur (`auditor`) et de la plateforme — PAS d'un simple propriétaire de projet. Remplace
   * `ownerOnly` pour l'entrée qui le porte.
   */
  administrationOrganisation?: boolean
  /** Lot USR : un item `ownerOnly` que l'auditeur lit aussi (journal, conformité). */
  lectureAuditeur?: boolean
  /** Rôle minimal requis pour voir cet item (défaut : viewer). */
  minRole?: Role
  /** Une des 14 entrées essentielles du mode Découverte (§ 4.11, niveau projet ; + « Décisions à prendre », v05x-ux-vague1). */
  essentiel?: boolean
  /** Sous-titre d'étape du groupe (« ① Cadrage ») : non focalisable, collant. */
  etape?: string
  /** Autres motifs (`chemin` ou `chemin?requête`) qui désignent la même entrée. */
  alias?: string[]
  etat?: EtatEntree
  /** Vague du lot U6b qui livre l'écran (entrée `a-venir`). */
  vague?: number
  /** Entrée bêta NON livrée par sa vague : ce qui manque, dit tel quel sur l'écran d'attente. */
  attente?: string
  /** Où la fonction vit aujourd'hui, pour une entrée pas encore construite. */
  repli?: { path: string; label: string }
  /** Hors barre latérale : atteint par la palette, les liens et le fil d'Ariane seulement. */
  horsBarre?: boolean
}

/** Un élément de PREMIER niveau de la barre : une entrée seule, ou un groupe repliable. */
export interface NavGroup {
  /** Identifiant stable (tests, `aria-controls`, mémoire du repli) : jamais traduit. */
  id: string
  label: string
  icon: LucideIcon
  niveau: Niveau
  type: 'entree' | 'groupe'
  items: NavItem[]
}

export interface NiveauNav {
  id: Niveau
  /** Titre affiché en tête de la barre (« Projet »). */
  label: string
  /** Nom du landmark `nav` de la barre latérale. */
  navLabel: string
  groupes: NavGroup[]
}

const MARQUE_05X = 'conçu, livré après la v0.5.0'

/** Accueil : premier lien de la barre, à tous les niveaux. */
export const ACCUEIL: NavItem = {
  label: 'Accueil', path: '/', navKey: 'nav.dashboard', icon: Home, end: true,
  description: 'Votre vue par profil : ce qui attend votre décision et où en est le travail',
}

/** Écrans transverses : barre haute, Aide ou liens des écrans ; jamais dans une barre latérale. */
export const TRANSVERSES: NavItem[] = [
  ACCUEIL,
  { label: 'Boîte d’attention', path: '/attention', navKey: 'nav.attention', icon: Inbox, horsBarre: true,
    description: 'Ce qui attend une décision, avec son âge : approbations, colonie, anomalies à vérifier, dérogations' },
  // Lot AG (SRF-TRV-055) : « Mon travail », mes items et décisions dans tous mes projets ; bloc en
  // tête de l'Accueil, écran complet ici.
  { label: 'Mon travail', path: '/mon-travail', navKey: 'nav.monTravail', icon: ListTodo, horsBarre: true,
    description: 'Mes items, anomalies, décisions et campagnes, dans tous mes projets, par statut et échéance' },
  // FND-V050-050 (SRF-ACC-014, hors navigation) : arrivée par le lien d'invitation, ou depuis la
  // boîte d'attention et l'accueil d'un porteur sans espace.
  { label: 'Invitations', path: '/invitations', navKey: 'nav.invitations', icon: MailOpen, horsBarre: true,
    description: 'Rejoindre un espace : vos invitations en attente ; un lien d’invitation s’ouvre ici' },
  { label: 'Notifications', path: '/notifications', navKey: 'nav.notifications', icon: Bell, horsBarre: true,
    description: 'Vos @mentions et alertes ; marquer comme lu' },
  { label: 'Délais de décision', path: '/hitl-cockpit', navKey: 'nav.hitlCockpit', icon: TimerReset, horsBarre: true,
    description: 'Toutes les décisions humaines en attente, triées par ancienneté, et leur temps de décision' },
  { label: 'Prise en main', path: '/guide', navKey: 'nav.guide', icon: Compass, horsBarre: true,
    description: 'Le cycle de bout en bout, cliquable — revenez-y quand vous voulez' },
  // Lot NAV 0.5.x (registre n° 12, A3 G14) : accueil de l'administration (super-administrateur,
  // administrateur d'organisation) — ce qui attend une décision d'administration et les raccourcis.
  { label: 'Accueil administration', path: '/administration', navKey: 'nav.administration', icon: ShieldCheck,
    horsBarre: true, ownerOnly: true,
    description: 'Demandes d’accès, invitations, organisations suspendues, santé et raccourcis d’administration' },
]

const entree = (niveau: Niveau, id: string, item: NavItem): NavGroup =>
  ({ id, label: item.label, icon: item.icon, niveau, type: 'entree', items: [item] })

// ---------------------------------------------------------------------------------------------
// Projet (§ 4.3.5)
// ---------------------------------------------------------------------------------------------
const PROJET: NavGroup[] = [
  {
    niveau: 'projet', type: 'entree', id: 'vue-projet', label: 'Vue projet', icon: Compass,
    items: [
      { label: 'Vue projet', path: '/projet', navKey: 'nav.projet', icon: Compass, essentiel: true,
        alias: ['/projet?vue=apercu'],
        description: 'Santé, cycle, équipe, missions et prochaine action du projet' },
      // Onglets de la Vue projet (vague 2) : atteints par la palette et les liens en attendant.
      { label: 'Cycle et processus', path: '/projet?vue=processus', navKey: 'nav.processus', icon: ListChecks, horsBarre: true,
        description: 'Exécutions de processus du projet : portes, approbations et reprises' },
      { label: 'Activité', path: '/timeline', navKey: 'nav.timeline', icon: Activity, horsBarre: true,
        description: 'Fil chronologique unifié des événements du projet' },
      { label: 'Indicateurs', path: '/scorecard', navKey: 'nav.scorecard', icon: Gauge, horsBarre: true,
        description: 'Livraison, DORA, IA, gouvernance et objectifs de service calculés des données' },
      // Lot NAV 0.5.x (registre n° 9, 10) : les membres du projet ont une VRAIE page, lisible par tout
      // membre (le lien « Membres et équipes » de la Vue projet menait à /projet/equipe, une 404).
      { label: 'Membres du projet', path: '/projet/membres', navKey: 'nav.projetMembres', icon: Users, horsBarre: true,
        description: 'Qui travaille sur ce projet, avec quel rôle ; équipes ; demandes d’accès à décider' },
    ],
  },
  {
    niveau: 'projet', type: 'groupe', id: 'concevoir', label: 'Concevoir', icon: FileText,
    items: [
      // Lot NAV 0.5.x (A2 § 1.1, constat 9) : lisibles du lecteur — l'écran retire lui-même les actions.
      { label: 'Demandes', path: '/demandes', navKey: 'nav.demandes', icon: Lightbulb, essentiel: true,
        etape: '① Cadrage', description: 'Déposer une demande — son premier état est l’idée — et lancer son cadrage' },
      // v05x-ux-vague1 (L7, UXA-DC03) : la file de décisions est VISIBLE dans Concevoir, avec le
      // compteur des étapes que JE peux faire (AppSidebar) ; même chemin et même navKey qu'avant
      // (contrat `NotificationVisibility`), elle reste atteignable par la palette.
      { label: 'Décisions à prendre', path: '/approvals', navKey: 'nav.approvals', icon: ClipboardCheck, essentiel: true,
        etape: '① Cadrage', description: 'Relire, contrôler et décider les documents produits : qui doit agir, et pourquoi' },
      { label: 'Sources et connaissances', path: '/ingestion', navKey: 'nav.ingestion', icon: Upload,
        etape: '① Cadrage', description: 'Téléverser des documents et interroger la base documentaire' },
      { label: 'Assistant du projet', path: '/chat', navKey: 'nav.chat', icon: MessagesSquare,
        etape: '① Cadrage', description: 'Dialoguer avec l’assistant dans le contexte du projet' },
      { label: 'Décisions et profil de pile', path: '/decisions', navKey: 'nav.decisions', icon: Scale, essentiel: true,
        etape: '② Choix technologiques', description: 'Décisions d’architecture (ADR), matrice et profil de pile du projet' },
      { label: 'Choix et baseline', path: '/technologies', navKey: 'nav.technologies', icon: Radar,
        etape: '② Choix technologiques',
        description: 'Baseline technologique du projet, héritage par catégorie et profil de génération' },
      // Lot AR 0.5.x (revue par métier, manque 21 : AR-09, AR-10, AR-14).
      { label: 'Architecture', path: '/architecture', navKey: 'nav.architecture', icon: Blocks,
        etape: '② Choix technologiques',
        description: 'Composants, API, dépendances (SBOM) et vulnérabilités de l’application livrée, par version' },
      // Lot T3 0.5.x (SRF-PRJ-014) : écran livré, sorti de l'état « 0.5.x ».
      { label: 'Demandes de changement', path: '/technologies/changements', navKey: 'nav.technologiesChangements', icon: GitBranch,
        etape: '② Choix technologiques',
        description: 'Changer une valeur technologique : demande de changement ou propagation' },
      { label: 'Design', path: '/design', navKey: 'nav.design', icon: Palette, essentiel: true,
        etape: '③ Design', description: 'Parcours d’écrans, maquettes et charte, puis la conformité d’une révision' },
      { label: 'Spécifications', path: '/specifications', navKey: 'nav.specifications', icon: FileText, essentiel: true,
        minRole: 'contributor', etape: '④ Spécification',
        description: 'Spécifications approuvées, reçu Spec Gate, édition en nouvelle version et suite du parcours' },
      // navKey du contrat serveur `NotificationVisibility` : une notification ouvre ces écrans.
      { label: 'Déposer une idée', path: '/ideas', navKey: 'nav.ideas', icon: Lightbulb, minRole: 'contributor', horsBarre: true,
        etape: '① Cadrage', description: 'Nouvelle demande : décrire l’idée, puis lancer son cadrage' },
      { label: 'Éditeur de spécification', path: '/editor', navKey: 'nav.editor', icon: FileText, minRole: 'contributor', horsBarre: true,
        etape: '④ Spécification', description: 'Éditer une spécification approuvée en nouvelle version, Spec Gate' },
      // Lot FON-B (SRF-DEM-010) : demandes de modification DMS-n — hors barre, atteintes depuis
      // Spécifications et l'éditeur.
      { label: 'Demandes de modification', path: '/specifications/modifications', navKey: 'nav.specificationsModifications', icon: FileText,
        horsBarre: true, etape: '④ Spécification',
        description: 'Demandes de modification des spécifications approuvées : justification, impact estimé, décision' },
      { label: 'Exigences héritées', path: '/exigences', navKey: 'nav.exigences', icon: ShieldCheck,
        etape: '④ Spécification', description: 'Héritage du référentiel, renforcement, contrôle de présence et dérogations du projet' },
      { label: 'Artefacts publiés', path: '/artifacts', navKey: 'nav.artifacts', icon: Package,
        etape: '④ Spécification', description: 'Index des artefacts publiés, avec empreinte et provenance' },
      { label: 'Comparer des modèles', path: '/compare', navKey: 'nav.compare', icon: GitCompareArrows,
        etape: '④ Spécification', description: 'Un prompt, plusieurs modèles : comparer leurs réponses' },
    ],
  },
  {
    niveau: 'projet', type: 'groupe', id: 'planifier', label: 'Planifier', icon: CalendarRange,
    items: [
      { label: 'Backlog', path: '/projet?vue=tableau', navKey: 'nav.backlog', icon: Columns3, essentiel: true,
        etape: '⑤ Planification',
        alias: ['/projet?vue=liste', '/projet?vue=hierarchie', '/projet?vue=graphe', '/projet?vue=pret-colonie', '/items'],
        description: 'Items du projet : tableau, liste, hiérarchie, graphe et prêt pour la colonie' },
      { label: 'Sprints', path: '/projet?vue=sprints', navKey: 'nav.sprints', icon: IterationCcw, essentiel: true,
        etape: '⑤ Planification', description: 'Sprints, planificateur et burndown' },
      { label: 'Roadmap', path: '/projet?vue=roadmap', navKey: 'nav.roadmap', icon: CalendarRange,
        etape: '⑤ Planification', description: 'Epics et features datés, versions en jalons, sprints' },
      { label: 'Dépendances inter-projets', path: '/dependencies', navKey: 'nav.dependencies', icon: Network,
        etape: '⑤ Planification', description: 'Déclarer les dépendances entre projets' },
      // Lot FON-B : étiquettes, composants et versions de classement — hors barre, atteint depuis la
      // fiche d'un item (« Gérer les étiquettes, composants et versions du projet »).
      { label: 'Classement du projet', path: '/projet/classement', navKey: 'nav.classement', icon: List, horsBarre: true,
        etape: '⑤ Planification', description: 'Étiquettes, composants et versions de classement : renommer, dater, supprimer' },
    ],
  },
  {
    niveau: 'projet', type: 'groupe', id: 'construire', label: 'Construire', icon: Hammer,
    items: [
      { label: 'Missions', path: '/missions', navKey: 'nav.missions', icon: Bot, essentiel: true,
        etape: '⑥ Développement par les agents', description: 'Lancer une mission de la colonie, la piloter et lire son dossier de preuves' },
      { label: 'Supervision des agents', path: '/supervision', navKey: 'nav.supervision', icon: Eye,
        etape: '⑥ Développement par les agents',
        description: 'Ce qui tourne dans le projet : missions, castes au travail, agents ; coût estimé et consommé étiquetés séparément' },
      { label: 'Agents personnalisés', path: '/workflow', navKey: 'nav.workflow', icon: Workflow, minRole: 'contributor',
        etape: '⑥ Développement par les agents', description: 'Exécuter un agent personnalisé et suivre sa trace' },
      { label: 'Journal des jobs', path: '/history', navKey: 'nav.history', icon: History,
        etape: '⑥ Développement par les agents', description: 'Tous les jobs passés et leur boîte de verre' },
      { label: 'Retours sur les générations', path: '/feedback', navKey: 'nav.feedback', icon: Star,
        etape: '⑥ Développement par les agents', description: 'Noter les sorties de l’IA et lire les retours' },
    ],
  },
  {
    niveau: 'projet', type: 'groupe', id: 'verifier', label: 'Vérifier', icon: FlaskConical,
    items: [
      { label: 'Tests', path: '/tests', navKey: 'nav.tests', icon: FlaskConical, essentiel: true,
        etape: '⑦ Tests et qualité', description: 'Cas, plans, non-régression, campagnes et résultats' },
      { label: 'Anomalies', path: '/anomalies', navKey: 'nav.anomalies', icon: Bug, essentiel: true,
        etape: '⑦ Tests et qualité', description: 'Déclarer, trier, corriger, vérifier puis fermer chaque anomalie avec sa preuve' },
      { label: 'Couverture et traçabilité', path: '/traceability', navKey: 'nav.traceability', icon: Waypoints,
        etape: '⑦ Tests et qualité', description: 'Exigence → code → test → anomalie, et les trous de couverture' },
      { label: 'Recette métier', path: '/recette', navKey: 'nav.recette', icon: BadgeCheck, essentiel: true,
        etape: '⑦ Tests et qualité',
        description: 'Validation d’une version par le métier : accepter ou refuser, remarques en anomalies' },
      { label: 'Tableau de bord qualité', path: '/qualite', navKey: 'nav.qualite', icon: Gauge,
        etape: '⑦ Tests et qualité', description: 'Échappement, réouverture, délai de correction' },
    ],
  },
  {
    niveau: 'projet', type: 'groupe', id: 'livrer', label: 'Livrer et exploiter', icon: Rocket,
    items: [
      { label: 'Cockpit de livraison', path: '/delivery', navKey: 'nav.delivery', icon: Rocket, minRole: 'contributor',
        etape: '⑧ Livraison', description: 'Déploiements et pipelines en cours' },
      { label: 'Versions', path: '/versions', navKey: 'nav.versions', icon: Tag, essentiel: true,
        etape: '⑧ Livraison', description: 'Préparer une version, savoir si elle est prête, l’approuver par environnement et la livrer' },
      { label: 'Aperçus', path: '/apercu', navKey: 'nav.apercu', icon: AppWindow, essentiel: true,
        etape: '⑧ Livraison', description: 'Voir, comparer et annoter une révision de l’application' },
      // Lot I 0.5.x (SRF-LIV-017/018/019, FND-V050-217) : écran construit, sorti de « à venir ».
      { label: 'Publication du code', path: '/publication', navKey: 'nav.publication', icon: PackageCheck,
        etape: '⑧ Livraison', description: 'Dépôt GitLab par révision, précondition, environnements reliés' },
      { label: 'Pipelines', path: '/pipelines', navKey: 'nav.pipelines', icon: Workflow, minRole: 'contributor',
        etape: '⑧ Livraison', description: 'Définir et lancer des pipelines, relier un run à son déploiement' },
      // Lot RP 0.5.x (revue par métier, manque 22 : RM-09).
      { label: 'Calendrier des mises en production', path: '/calendrier', navKey: 'nav.calendrier', icon: CalendarClock,
        etape: '⑧ Livraison', description: 'Déploiements réalisés et prévus, dates cibles des versions et périodes de gel' },
      { label: 'Environnements et déploiements', path: '/runops', navKey: 'nav.runops', icon: Server, minRole: 'contributor',
        etape: '⑨ Exploitation', description: 'Environnements, déploiements, retour arrière, budget d’erreur et continuité' },
      // FND-V050-050 (SRF-PRO-050) : catalogue SLO du projet et budget d'erreur par objectif.
      // Lot NAV 0.5.x (A2 § 2.3) : le catalogue SLO se lit sous /admin/slo-catalog (administration) — un
      // contributeur ou un lecteur ne se voit plus proposer une entrée qui répond 403.
      { label: 'Objectifs de service', path: '/runops?vue=slo', navKey: 'nav.objectifsService', icon: Gauge, ownerOnly: true,
        etape: '⑨ Exploitation', description: 'Cible, fenêtre et budget d’erreur de chaque objectif de service du projet' },
      { label: 'Application en service', path: '/exploitation', navKey: 'nav.exploitation', icon: HeartPulse,
        etape: '⑨ Exploitation', description: 'Version en production, sonde et documentation' },
      // Lot INC 0.5.x (revue par métier, manque 18 : RM-14, RM-15) : santé des environnements livrés,
      // objectifs de service métier et leur marge — le dépassement avertit, il ne bloque rien.
      { label: 'Supervision de l’application', path: '/exploitation/supervision', navKey: 'nav.supervisionApplication',
        icon: Activity, etape: '⑨ Exploitation',
        description: 'Santé des environnements livrés, objectifs de service métier et marge d’incidents' },
      // Lot INC 0.5.x (revue par métier, manque 6 : RM-12, RM-13) : incidents de production et post-mortem.
      { label: 'Incidents', path: '/incidents', navKey: 'nav.incidents', icon: Siren, etape: '⑨ Exploitation',
        description: 'Incidents de production : déclaration, chronologie, résolution et post-mortem sans blâme' },
      { label: 'Documentation', path: '/documentation', navKey: 'nav.documentation', icon: BookText,
        etape: '⑨ Exploitation', description: 'Documentation vivante du projet, lisible à l’écran' },
    ],
  },
  entree('projet', 'reglages', {
    label: 'Réglages du projet', path: '/admin', navKey: 'nav.admin', icon: Settings, ownerOnly: true,
    description: 'Membres, modèles et agents autorisés, quotas et intégrations du projet',
  }),
]

// ---------------------------------------------------------------------------------------------
// Organisation (§ 4.3.3) — 12 éléments + Accueil = 13 (AC-N1, ARB-r2-21)
// ---------------------------------------------------------------------------------------------
const ORGANISATION: NavGroup[] = [
  entree('organisation', 'vue-ensemble', {
    label: 'Vue d’ensemble', path: '/tableau-de-bord', navKey: 'nav.tableauDeBord', icon: LayoutDashboard,
    alias: ['/org'],
    // v8e-p07 (E18, HD-35) : `GET /api/v1/portfolio` exige `organisation.membres.voir` (administration de
    // l'organisation, auditeur) : l'entrée suit ce prédicat, un membre ordinaire n'y est plus mené vers un 403.
    administrationOrganisation: true,
    description: 'Santé répartie, qualité, livraison et attention des projets de l’organisation',
  }),
  // Lot ORG 0.5.x (vague 4, registre n° 1) : la fiche de l'organisation courante — identité,
  // gouvernance, dérogations, intégrations. Entrée de l'ADMINISTRATION de l'organisation ; tout
  // membre y arrive aussi en lecture par le nom de l'organisation (barre de contexte).
  entree('organisation', 'mon-organisation', {
    label: 'Mon organisation', path: '/org/fiche', navKey: 'nav.orgFiche', icon: Building2,
    // Intégration ORG × USR : même prédicat que l'annuaire (administrateur d'organisation `org_admin`,
    // auditeur en lecture, plateforme) — plus un simple propriétaire de projet (ARB-SEC-USR-01).
    administrationOrganisation: true, reserveAdministration: true,
    description: 'Identité de l’organisation, réglages de gouvernance, dérogations et intégrations (IA, connecteurs)',
  }),
  entree('organisation', 'programmes', {
    label: 'Programmes', path: '/programs', navKey: 'nav.programs', icon: Layers,
    description: 'Les programmes dont vous êtes membre ; créer un programme',
  }),
  entree('organisation', 'projets', {
    label: 'Projets', path: '/org/projets', navKey: 'nav.orgProjets', icon: Boxes,
    description: 'Projets de l’organisation : programme, santé, pile ; créer, rattacher, archiver',
  }),
  {
    niveau: 'organisation', type: 'groupe', id: 'referentiel', label: 'Référentiel et technologies', icon: Landmark,
    items: [
      { label: 'Référentiel d’exigences', path: '/organisation', navKey: 'nav.organisation', icon: Landmark,
        description: 'Définition de fini, exigences et contrôles de l’organisation' },
      // Lot ORG 0.5.x (A1 M13) : les dérogations de TOUTE l'organisation, rangées avec le référentiel
      // auquel elles dérogent (la place libérée au premier niveau revient à « Mon organisation »).
      { label: 'Dérogations', path: '/org/derogations', navKey: 'nav.derogations', icon: Scale,
        description: 'Toutes les dérogations de l’organisation : par projet, risque, échéance et état' },
      { label: 'Catalogue technologique', path: '/org/technologies', navKey: 'nav.orgTechnologies', icon: Radar,
        description: 'Les choix technologiques dynamiques de l’organisation : catalogue, catégories, radar' },
      // Lot G 0.5.x (ORG-07, ORG-08, ORG-09 ; SRF-ORG-013) : livrés.
      { label: 'Règles en trois temps', path: '/org/regles', navKey: 'nav.orgRegles', icon: ListChecks,
        description: 'Observer, avertir, bloquer : le temps de chaque règle, sa mesure et ses décisions tracées' },
      { label: 'Chemin doré', path: '/org/chemin-dore', navKey: 'nav.orgCheminDore', icon: Compass,
        description: 'Configuration recommandée versionnée et projets hérités en écart (alignement proposé)' },
      { label: 'Santé de la conformité', path: '/org/sante', navKey: 'nav.orgSante', icon: HeartPulse,
        description: 'Par projet : écarts, règles qui avertissent ou bloquent, dérogations, livraisons, anomalies' },
      // Lot D 0.5.x (B-502, DES-10, SRF-APP-015) : la charte graphique héritée par les projets.
      { label: 'Charte de l’organisation', path: '/org/charte', navKey: 'nav.orgCharte', icon: Palette,
        description: 'La charte graphique que les projets héritent : recommandée ou imposée, et ses dérogations' },
    ],
  },
  entree('organisation', 'gabarits', {
    label: 'Gabarits', path: '/config', navKey: 'nav.config', icon: SlidersHorizontal, ownerOnly: true,
    description: 'Gabarits : modèle d’item, cycles de vie, agents, modèles et routage, modes',
  }),
  entree('organisation', 'gouvernance', {
    // Lot NAV 0.5.x (A2 § 2.3) : l'arrêt d'urgence de l'organisation se lit sous /admin/ai/kill-switch —
    // réservé à l'administration, comme les autres entrées de ce rang.
    label: 'Gouvernance et approbations', path: '/org/gouvernance', navKey: 'nav.gouvernance', icon: ShieldCheck,
    ownerOnly: true, reserveAdministration: true,
    description: 'Modes d’approbation et arrêt d’urgence de l’IA à la portée de l’organisation',
  }),
  entree('organisation', 'harnais', {
    // FND-V050-055 : la vue « Agents personnalisés » (provenance, revue, promotion) est livrée.
    label: 'Harnais et agents', path: '/org/harnais', navKey: 'nav.orgHarnais', icon: Bot,
    description: 'Castes, agents personnalisés et leur promotion, finalités IA, serveurs MCP, connexions et modèles, supervision',
  }),
  entree('organisation', 'axes', {
    label: 'Axes de portefeuille', path: '/taxonomy', navKey: 'nav.taxonomy', icon: Network,
    description: 'Portefeuille, domaine, capacité — le travail qui s’y rattache et la santé répartie par axe',
  }),
  // Lot USR (registre n° 2 à 7) : l'entrée « Accès » devient le groupe « Utilisateurs et accès »
  // — annuaire, invitations, sécurité de connexion, matrice des droits (et sa revue des refus) —,
  // réservé à l'administration de l'organisation et à ses auditeurs (≤ 13 entrées, AC-N1).
  {
    niveau: 'organisation', type: 'groupe', id: 'acces', label: 'Utilisateurs et accès', icon: KeyRound,
    items: [
      { label: 'Utilisateurs', path: '/org/utilisateurs', navKey: 'nav.orgUtilisateurs', icon: Users,
        administrationOrganisation: true, reserveAdministration: true,
        description: 'Annuaire de l’organisation : rôles, statuts, appartenances, départs et réactivations' },
      { label: 'Invitations', path: '/org/invitations', navKey: 'nav.orgInvitations', icon: MailOpen,
        administrationOrganisation: true, reserveAdministration: true,
        description: 'Faire entrer une personne nouvelle : invitation par e-mail et lien d’activation' },
      { label: 'Sécurité de connexion', path: '/org/securite', navKey: 'nav.orgSecurite', icon: ShieldCheck,
        administrationOrganisation: true, reserveAdministration: true,
        description: 'Second facteur obligatoire, durée de session, domaines autorisés, mot de passe' },
      { label: 'Accès : rôles et politiques', path: '/org/acces', navKey: 'nav.acces', icon: KeyRound,
        administrationOrganisation: true, reserveAdministration: true,
        description: 'Matrice des rôles, politiques d’accès et simulateur' },
      { label: 'Revue des refus d’habilitation', path: '/org/acces/revue', navKey: 'nav.orgAccesRevue', icon: ListChecks,
        administrationOrganisation: true, reserveAdministration: true, horsBarre: true,
        description: 'Ce que la matrice refuse ou refuserait, avant et après le mode bloquant' },
    ],
  },
  entree('organisation', 'quotas', {
    label: 'Quotas et palier', path: '/quotas', navKey: 'nav.quotas', icon: Gauge,
    description: 'Consommation et limites courantes de votre espace et des modèles IA',
  }),
  entree('organisation', 'conformite', {
    label: 'Conformité et audit', path: '/org/conformite', navKey: 'nav.conformite', icon: FileCheck,
    ownerOnly: true, reserveAdministration: true, lectureAuditeur: true,
    description: 'Rétention, purge, export d’audit et historique des arrêts d’urgence',
  }),
]

// ---------------------------------------------------------------------------------------------
// Programme (§ 4.3.4) — l'essentiel est livré par la vague 8
// ---------------------------------------------------------------------------------------------
const livreProgramme = (id: string, label: string, path: string, navKey: string, icon: LucideIcon,
  description: string): NavGroup => entree('programme', id, { label, path, navKey, icon, description })

const PROGRAMME: NavGroup[] = [
  livreProgramme('vue-programme', 'Vue programme', '/programme', 'nav.programme', Layers,
    'Objectif, jalon, santé répartie, étape des projets, train et dépendances bloquantes'),
  livreProgramme('membres', 'Membres du programme', '/programme/membres', 'nav.programmeMembres', Users,
    'Lister, ajouter, retirer les membres et leurs rôles'),
  livreProgramme('projets', 'Projets du programme', '/programme/projets', 'nav.programmeProjets', Boxes,
    'Rattacher, détacher ; étape, pile et santé des projets'),
  livreProgramme('roadmap', 'Roadmap et train', '/programme/roadmap', 'nav.programmeRoadmap', CalendarRange,
    'Sprints, jalons et versions du programme'),
  // SRF-PGM-015 / SRF-PRO-040 : lancer une initiative et lire la trace des initiatives lancées.
  livreProgramme('backlog', 'Backlog et initiatives', '/programme/backlog', 'nav.programmeBacklog', ListTree,
    'Cadrage ou génération d’epics et trace des initiatives lancées'),
  entree('programme', 'dependances', {
    label: 'Dépendances inter-projets', path: '/programme/dependances', navKey: 'nav.programmeDependances', icon: Network,
    description: 'Carte des dépendances entre les projets du programme',
  }),
  // FND-V050-050 (SRF-PRO-048) : le référentiel du programme a son écran.
  livreProgramme('gouvernance', 'Gouvernance et standards', '/programme/gouvernance', 'nav.programmeGouvernance', ShieldCheck,
    'Référentiel du programme, qui renforce celui de l’organisation'),
  entree('programme', 'technologies', {
    label: 'Choix technologiques du programme', path: '/programme/technologies', navKey: 'nav.programmeTechnologies',
    icon: Radar, description: 'Héritage du catalogue, standard du programme et baseline',
  }),
  entree('programme', 'changements', {
    label: 'Demandes de changement du programme', path: '/programme/technologies?vue=changements',
    // Lot T3 0.5.x (SRF-PGM-014) : vue livrée, sortie de l'état « 0.5.x ».
    navKey: 'nav.programmeChangements', icon: GitBranch,
    description: 'Portefeuille des demandes de changement des projets du programme',
  }),
  livreProgramme('versions', 'Versions du programme', '/programme/versions', 'nav.programmeVersions', Tag,
    'Le train des versions, en lecture agrégée'),
  // Lot RP 0.5.x (revue par métier, manque 19 : SP-10, SP-11) : rapport d'avancement exportable.
  livreProgramme('rapport', 'Rapport d’avancement', '/programme/rapport', 'nav.programmeRapport', FileBarChart,
    'Objectif, risques, vélocité réelle et prévision de fin ; export CSV et impression'),
  // Lot P 0.5.x (SRF-PGM-011) : coût et capacité du programme, estimé ≠ consommé.
  livreProgramme('capacite', 'Coût et capacité', '/programme/capacite', 'nav.programmeCapacite', Receipt,
    'Minutes GPU consommées et estimées, missions en cours par projet'),
  // SRF-PGM-008 : réglages lus par les membres, modifiés par un propriétaire du programme.
  livreProgramme('reglages', 'Réglages du programme', '/programme/reglages', 'nav.programmeReglages', Settings,
    'Nom, propriétaire, statut, gabarit par défaut'),
]

// ---------------------------------------------------------------------------------------------
// Plateforme (§ 4.3.2) — super-administrateur seul
// ---------------------------------------------------------------------------------------------
const plateforme = (id: string, item: Omit<NavItem, 'superAdmin'>): NavGroup =>
  entree('plateforme', id, { ...item, superAdmin: true })

const PLATEFORME: NavGroup[] = [
  plateforme('sante', { label: 'Santé de la plateforme', path: '/plateforme/sante', navKey: 'nav.plateformeSante',
    icon: HeartPulse, description: 'Santé par organisation, versions et profil du poste (GPU, moteur, modèles)' }),
  plateforme('file-gpu', { label: 'File GPU de la plateforme', path: '/plateforme/file-gpu', navKey: 'nav.plateformeFileGpu',
    icon: Cpu,
    description: 'Qui utilise le GPU maintenant, dans quel ordre, et ce qui est suspendu' }),
  plateforme('organisations', { label: 'Organisations', path: '/plateforme/organisations', navKey: 'nav.plateformeOrganisations',
    icon: Building2, description: 'Provisionner, palier, suspendre' }),
  plateforme('ia', { label: 'Fournisseurs et modèles d’IA', path: '/plateforme/ia', navKey: 'nav.plateformeIa',
    icon: Globe2, description: 'Connexions, modèles, raccordements, observabilité, canaux' }),
  plateforme('arret-ia', { label: 'Arrêt d’urgence de l’IA', path: '/plateforme/arret-ia', navKey: 'nav.plateformeArretIa',
    icon: OctagonX, description: 'Portée plateforme, arrêts actifs par organisation et historique commun' }),
  plateforme('executants', { label: 'Exécutants et file', path: '/plateforme/executants', navKey: 'nav.plateformeExecutants',
    icon: Server, description: 'Exécutants, signes de vie et quarantaine' }),
  {
    niveau: 'plateforme', type: 'groupe', id: 'harnais', label: 'Harnais', icon: Bot,
    items: [
      // Lot T4 (SRF-ADM-040/041/042) : écran livré, sorti de l'état « à venir ».
      { label: 'Cartes, castes et modèles du poste', path: '/plateforme/harnais', navKey: 'nav.plateformeHarnais', icon: Bot,
        superAdmin: true,
        description: 'Registre des cartes par caste, promotion, reçus, budget VRAM' },
      { label: 'Profils de génération qualifiés', path: '/plateforme/generation', navKey: 'nav.plateformeGeneration',
        icon: FolderKanban, superAdmin: true,
        description: 'Profils qualifiés, cas dorés et âge du dernier reçu valide' },
    ],
  },
  // Lot PL (SRF-PLT-005) : écran livré, sorti de l'état « 0.5.x ».
  plateforme('continuite', { label: 'Continuité de la plateforme DoIt', path: '/plateforme/continuite',
    navKey: 'nav.plateformeContinuite', icon: Archive,
    description: 'Sauvegarde de l’état de DoIt lui-même, rétention, essai de restauration' }),
  plateforme('journal', { label: 'Journal du super-administrateur', path: '/plateforme/journal', navKey: 'nav.plateformeJournal',
    icon: List, description: 'Toute action de la plateforme sur une organisation' }),
  // Lot PL (SRF-PLT-009) : écran livré, sorti de l'état « 0.5.x ».
  plateforme('acces-exceptionnels', { label: 'Accès exceptionnels actifs', path: '/plateforme/acces-exceptionnels',
    navKey: 'nav.plateformeAccesExceptionnels', icon: KeyRound,
    description: 'Sessions d’accès exceptionnel en cours et révocation' }),
  plateforme('decisions', { label: 'Décisions de l’outil', path: '/plateforme/decisions', navKey: 'nav.plateformeDecisions',
    icon: Scale, description: 'Décisions d’architecture de DoIt' }),
  plateforme('audit', { label: 'Recommandations d’audit', path: '/audit-roadmap', navKey: 'nav.auditRoadmap',
    icon: ClipboardCheck, description: 'Recommandations d’audit, cycles, work items et niveau de preuve' }),
]

export const NIVEAUX: NiveauNav[] = [
  { label: 'Projet', id: 'projet', navLabel: 'Navigation du projet', groupes: PROJET },
  { label: 'Programme', id: 'programme', navLabel: 'Navigation du programme', groupes: PROGRAMME },
  { label: 'Organisation', id: 'organisation', navLabel: 'Navigation de l’organisation', groupes: ORGANISATION },
  { label: 'Plateforme', id: 'plateforme', navLabel: 'Navigation de la plateforme', groupes: PLATEFORME },
]

/** Tous les éléments de premier niveau de tous les niveaux. */
export const NAV_GROUPS: NavGroup[] = NIVEAUX.flatMap((n) => n.groupes)

/** Liste à plat (transverses compris), pour la palette, le titre d'onglet et le fil d'Ariane. */
export const NAV_ITEMS: NavItem[] = [...TRANSVERSES, ...NAV_GROUPS.flatMap((g) => g.items)]

/** Libellé de la marque 0.5.x, lu par la barre, la palette et l'écran d'attente. */
export const MARQUE_0_5_X = MARQUE_05X

export function niveauParId(id: Niveau): NiveauNav {
  const n = NIVEAUX.find((x) => x.id === id)
  if (!n) throw new Error(`niveau inconnu : ${id}`)
  return n
}

// ---------------------------------------------------------------------------------------------
// Correspondance route → entrée
// ---------------------------------------------------------------------------------------------

/** Partie chemin d'un motif (sans sa requête). */
function cheminDe(motif: string): string {
  const i = motif.indexOf('?')
  return i < 0 ? motif : motif.slice(0, i)
}

/** Nombre de paires de la requête du motif présentes dans `search` ; -1 si l'une manque. */
function scoreRequete(motif: string, search: string): number {
  const i = motif.indexOf('?')
  if (i < 0) return 0
  const attendue = new URLSearchParams(motif.slice(i + 1))
  const reelle = new URLSearchParams(search)
  let n = 0
  for (const [cle, valeur] of attendue) {
    if (reelle.get(cle) !== valeur) return -1
    n += 1
  }
  return n
}

/** Préfixe de chemin à la frontière d'un segment : `/projet` couvre `/projet/x`, pas `/projets`. */
function couvre(prefixe: string, pathname: string): boolean {
  return pathname === prefixe || pathname.startsWith(prefixe.endsWith('/') ? prefixe : `${prefixe}/`)
}

/**
 * Écran courant à partir du chemin et de la requête. Un motif à requête l'emporte quand sa
 * requête correspond (le plus de paires d'abord) ; sinon le plus long préfixe de chemin.
 * `/` ne désigne que l'accueil (route exacte).
 */
export function currentNavItem(pathname: string, search = ''): NavItem | undefined {
  if (pathname === '/') return ACCUEIL
  let meilleur: { item: NavItem; score: number } | undefined
  for (const item of NAV_ITEMS) {
    if (item.end) continue
    for (const motif of [item.path, ...(item.alias ?? [])]) {
      const chemin = cheminDe(motif)
      let score: number
      if (motif.includes('?')) {
        if (chemin !== pathname) continue
        const s = scoreRequete(motif, search)
        if (s < 0) continue
        score = 10_000 + s
      } else {
        if (!couvre(chemin, pathname)) continue
        score = chemin.length
      }
      if (!meilleur || score > meilleur.score) meilleur = { item, score }
    }
  }
  return meilleur?.item
}

/** L'élément de premier niveau (entrée ou groupe) qui porte cet item. */
export function groupeDeItem(item: NavItem): NavGroup | undefined {
  return NAV_GROUPS.find((g) => g.items.includes(item))
}

/** Le niveau d'une route ; `undefined` pour une route transverse ou inconnue. */
export function niveauDuChemin(pathname: string, search = ''): Niveau | undefined {
  const item = currentNavItem(pathname, search)
  return item ? groupeDeItem(item)?.niveau : undefined
}

/** L'identifiant du groupe (ou de l'entrée) d'une route, pour déplier le bon groupe. */
export function sectionDuChemin(pathname: string, search = ''): string | undefined {
  const item = currentNavItem(pathname, search)
  return item ? groupeDeItem(item)?.id : undefined
}

// ---------------------------------------------------------------------------------------------
// Visibilité et modes
// ---------------------------------------------------------------------------------------------

/** Visibilité d'un item selon les rôles (owner ⊃ contributor ⊃ viewer ; `admin` = plateforme). */
export function itemVisiblePourRoles(item: NavItem, roles: string[]): boolean {
  const isOwner = roles.includes('workspace_owner')
  const isGlobalAdmin = roles.includes('admin')
  const isContributor = isOwner || roles.includes('workspace_contributor')
  if (item.superAdmin && !isGlobalAdmin) return false
  // Lot USR : l'administration de l'ORGANISATION (org_admin, auditeur, plateforme) — jamais un
  // simple propriétaire de projet (ARB-SEC-USR-01).
  if (item.administrationOrganisation) {
    return roles.includes('org_admin') || roles.includes('auditor') || isGlobalAdmin
  }
  if (item.lectureAuditeur && roles.includes('auditor')) return true
  // v05x-org-neuve : l'administration de l'organisation voit les entrées réservées même sans être
  // propriétaire d'un projet (« Gouvernance et approbations » : arrêt d'urgence de l'organisation, M8).
  if ((item.ownerOnly || item.reserveAdministration) && !isOwner && !isGlobalAdmin && !roles.includes('org_admin')) return false
  if (item.minRole === 'owner' && !isOwner) return false
  if (item.minRole === 'contributor' && !isContributor) return false
  return true
}

/** Les niveaux que ces rôles peuvent ouvrir : la Plateforme est au super-administrateur seul. */
export function niveauxVisibles(roles: string[]): NiveauNav[] {
  return NIVEAUX.filter((n) => n.id !== 'plateforme' || roles.includes('admin'))
}

/** Applique le rôle puis, AU NIVEAU PROJET, la divulgation progressive (§ 4.11). */
export function itemsDuGroupePourMode(group: NavGroup, roles: string[], mode: NavigationMode): NavItem[] {
  return group.items.filter((item) => !item.horsBarre && itemVisiblePourRoles(item, roles)
    && (mode === 'complete' || group.niveau !== 'projet' || item.essentiel === true))
}

/** Éléments de premier niveau affichés pour ce niveau (un groupe sans entrée visible disparaît). */
export function premierNiveau(niveau: Niveau, roles: string[], mode: NavigationMode): NavGroup[] {
  return niveauParId(niveau).groupes.filter((g) => itemsDuGroupePourMode(g, roles, mode).length > 0)
}

/** Nombre d'entrées de premier niveau de la barre, Accueil compris (AC-N1 : ≤ 13 à l'organisation). */
export function comptePremierNiveau(niveau: Niveau, roles: string[], mode: NavigationMode = 'complete'): number {
  return 1 + premierNiveau(niveau, roles, mode).length
}

/** Comptage des entrées du PROJET masquées par la Découverte (menu utilisateur, panneau). */
export function compteNavigation(
  roles: string[],
  mode: NavigationMode,
): { visible: number; affichees: number; masquees: number } {
  const groupes = niveauParId('projet').groupes
  const visible = groupes.flatMap((g) => g.items)
    .filter((item) => !item.horsBarre && itemVisiblePourRoles(item, roles)).length
  const affichees = groupes.flatMap((g) => itemsDuGroupePourMode(g, roles, mode)).length
  return { visible, affichees, masquees: Math.max(0, visible - affichees) }
}

// ---------------------------------------------------------------------------------------------
// Fil d'Ariane (§ 4.9, AC-N3) et titre d'onglet
// ---------------------------------------------------------------------------------------------

export interface SegmentFil {
  label: string
  /** Clé de traduction de l'objet (onglet de la Vue projet) ; sans elle, `label` est une donnée. */
  cle?: string
  /** Lien vers l'entrée ; absent pour un groupe (son écran dépend du rôle) et pour l'objet. */
  to?: string
  genre: 'groupe' | 'entree' | 'objet'
}

/**
 * `Groupe › Entrée › Objet`, 3 segments au plus, et JAMAIS le sélecteur de contexte
 * (ni organisation, ni programme, ni projet). Une entrée seule n'a pas de segment de groupe.
 */
export function segmentsFilAriane(pathname: string, search = '', objet?: string): SegmentFil[] {
  const item = currentNavItem(pathname, search)
  const segments: SegmentFil[] = []
  // Planche ProduitApercuProjet : « Projet › Vue projet › Aperçu » — le niveau, l'écran, l'onglet.
  const vue = new URLSearchParams(search).get('vue') ?? 'apercu'
  if (pathname === '/projet' && (ONGLETS_VUE_PROJET as readonly string[]).includes(vue)) {
    const onglet = VUES_PROJET.find((v) => v.cle === vue)!
    return [
      { label: 'Projet', genre: 'groupe' },
      { label: 'Vue projet', to: '/projet', genre: 'entree' },
      { label: onglet.label, cle: `projet.vue.${onglet.cle}`, genre: 'objet' },
    ]
  }
  if (item) {
    const groupe = groupeDeItem(item)
    if (groupe?.type === 'groupe') segments.push({ label: groupe.label, genre: 'groupe' })
    // Un onglet de la Vue projet (Activité, Indicateurs…) est situé sous la Vue projet.
    else if (groupe && groupe.items[0] !== item) segments.push({ label: groupe.items[0]!.label, to: groupe.items[0]!.path, genre: 'entree' })
    segments.push({ label: item.label, to: item.path, genre: 'entree' })
  }
  const objetResolu = objet ?? objetDuChemin(pathname)
  if (objetResolu) segments.push({ label: objetResolu, genre: 'objet' })
  return segments.slice(-3)
}

/** Objet désigné par la route elle-même (`/items/42`) ; les écrans en fourniront d'autres. */
export function objetDuChemin(pathname: string): string | undefined {
  const m = /^\/items\/([^/]+)$/.exec(pathname)
  return m ? `Item ${decodeURIComponent(m[1]!)}` : undefined
}

/**
 * Les vues de `/projet` (`?vue=...`), déclarées ICI — source unique — pour que `ProjetScreen`
 * (les onglets) et `titreDocument` (le titre d'onglet) lisent exactement le même libellé.
 */
export const VUES_PROJET = [
  { cle: 'apercu', label: 'Aperçu', icon: Compass },
  { cle: 'tableau', label: 'Tableau', icon: Columns3 },
  { cle: 'liste', label: 'Liste', icon: List },
  { cle: 'hierarchie', label: 'Hiérarchie', icon: Network },
  { cle: 'roadmap', label: 'Roadmap', icon: CalendarRange },
  { cle: 'sprints', label: 'Sprints', icon: IterationCcw },
  { cle: 'graphe', label: 'Graphe', icon: Share2 },
  { cle: 'pret-colonie', label: 'Prêt pour la colonie', icon: Rocket },
  { cle: 'processus', label: 'Cycle et processus', icon: ListChecks },
  { cle: 'activite', label: 'Activité', icon: History },
  { cle: 'indicateurs', label: 'Indicateurs', icon: Gauge },
] as const

/** Onglets de la Vue projet (planche ProduitApercuProjet) : Aperçu, Cycle et processus, Activité, Indicateurs. */
export const ONGLETS_VUE_PROJET = ['apercu', 'processus', 'activite', 'indicateurs'] as const

export type CleVueProjet = (typeof VUES_PROJET)[number]['cle']

const NOM_APPLICATION = 'DoIt'

/**
 * Titre d'onglet (WCAG 2.4.2) : « <écran> — DoIt ». `/projet` porte plusieurs vues dans le
 * même chemin : la vue (`?vue=`) est lue dans la requête, jamais devinée.
 */
export function titreDocument(pathname: string, search: string): string {
  if (pathname === '/projet') {
    const demandee = new URLSearchParams(search).get('vue')
    const vue = VUES_PROJET.find((v) => v.cle === demandee) ?? VUES_PROJET[0]
    return `Projet — ${vue.label} — ${NOM_APPLICATION}`
  }
  const item = currentNavItem(pathname, search)
  return item ? `${item.label} — ${NOM_APPLICATION}` : NOM_APPLICATION
}

// ---------------------------------------------------------------------------------------------
// Routes : écrans d'attente et déménagements (AC-T5)
// ---------------------------------------------------------------------------------------------

/** Entrées sans écran construit (bêta à venir ou 0.5.x) : le routeur leur monte l'écran d'attente. */
export const ENTREES_SANS_ECRAN: NavItem[] = NAV_ITEMS.filter((i) => i.etat !== undefined)

/**
 * Chemins (sans requête) à router vers l'écran d'attente, dédoublonnés. Un chemin qu'une entrée
 * livrée partage (v05x-t2 : `/programme/technologies`, dont seule la vue `?vue=changements` reste
 * à venir) a AUSSI sa route écrite, déclarée avant celles-ci dans `App.tsx` : à rang égal, le
 * routeur garde la première ; l'écran livré montre lui-même l'attente pour la vue non livrée.
 */
export const CHEMINS_SANS_ECRAN: string[] = [...new Set(ENTREES_SANS_ECRAN.map((i) => cheminDe(i.path)))]

/**
 * Anciennes adresses de la v0.4 (sections de l'administration) et leur nouvelle place : la
 * redirection garde un signet valide et l'écran d'arrivée dit une fois « Cet écran a déménagé ».
 */
export const DEMENAGEMENTS_ADMIN: Record<string, string> = {
  // O41 (parcours caisse) : un seul écran des membres fait autorité — celui du projet.
  membres: '/projet/membres',
  roles: '/org/acces?vue=roles',
  politiques: '/org/acces?vue=politiques',
  conformite: '/org/conformite',
  workers: '/plateforme/executants',
  plateformes: '/plateforme/ia',
  sante: '/plateforme/sante',
  organisations: '/plateforme/organisations',
}
